import './AnalysisForm.less';
import React, { useState } from 'react';
import { Button, Form, Collapse } from 'antd';
import UrlInput from '../shared/UrlInput';
import SectionsSelector from './SectionsSelector';
import CompetitorsInput from './CompetitorsInput';
import useGetPlanSettings from '../sales/hooks/useGetPlanSettings';
import { CompetitorInputState } from './types';
import { normalizeUrl } from '../shared/utils';
import { validate } from 'validate.js';
import { getSectionKeysFromTitles, urlValidation } from './utils';
import { useFetchAnalysisStructureQuery } from './service';
import useStartAnalysis from './hooks/useStartAnalysis';

const { Panel } = Collapse;

const initialUrlInputState = {
    urlSchema: 'https://',
    domain: '',
};

const initialCompetitorsInputState: Record<number, CompetitorInputState> = {};

const initialValidationState = {
    mainUrlValidation: '',
    competitorsValidation: [] as string[],
};

export default function AnalysisForm() {
    const { data: analysisStructure } = useFetchAnalysisStructureQuery({ fields: ['key', 'title', 'relevance'] });
    const { maxCompetitors } = useGetPlanSettings();

    const [urlInputState, setUrlInputState] = useState(initialUrlInputState);
    const [selectedSectionTitles, setSelectedSectionTitles] = useState<string[]>([]);
    const [competitorsInputState, setCompetitorsInputState] = useState(initialCompetitorsInputState);

    const [validationState, setValidationState] = useState(initialValidationState);

    const { startAnalysis } = useStartAnalysis();

    const handleUrlChange = (domain: string, urlSchema: string) => {
        setUrlInputState({ domain, urlSchema });
        setValidationState({ ...validationState, mainUrlValidation: '' });
    };

    const handleCompetitorsChange = (index: number, newDomain: string, newUrlSchema: string) => {
        const newCompetitors = { ...competitorsInputState };
        newCompetitors[index] = { domain: newDomain, urlSchema: newUrlSchema };

        const newValidations = [...validationState.competitorsValidation];
        newValidations[index] = '';

        setCompetitorsInputState(newCompetitors);
        setValidationState({ ...validationState, competitorsValidation: newValidations });
    };

    const handleSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let validationError = false;

        const mainUrl = normalizeUrl(urlInputState.domain, urlInputState.urlSchema);
        const mainUrlValidationError = validate({ url: mainUrl }, urlValidation);

        if (mainUrlValidationError) {
            setValidationState((validationState) => ({ ...validationState, mainUrlValidation: mainUrlValidationError.url[0] }));
            validationError = true;
        }

        const competitorUrls: string[] = [];
        const competitorsValidationErrors: string[] = [];

        for (const index in competitorsInputState) {
            const competitor = competitorsInputState[index];

            if (competitor.domain.length > 0) {
                const competitorUrl = normalizeUrl(competitor.domain, competitor.urlSchema);
                competitorUrls.push(competitorUrl);

                const validationError = validate({ url: competitorUrl }, urlValidation);

                if (validationError) {
                    competitorsValidationErrors[index] = validationError.url[0];
                }
            }
        }

        if (competitorsValidationErrors.length > 0) {
            setValidationState((validationState) => ({ ...validationState, competitorsValidation: competitorsValidationErrors }));
            validationError = true;
        }

        if (validationError || !analysisStructure) {
            return;
        }

        const uniqueCompetitorsUrls = competitorUrls.filter((url, index) => competitorUrls.indexOf(url) === index && url !== mainUrl);
        const selectedSectionKeys = getSectionKeysFromTitles(analysisStructure, selectedSectionTitles);

        startAnalysis(mainUrl, uniqueCompetitorsUrls, selectedSectionKeys);
    };

    return (
        <Collapse
            defaultActiveKey="analysis-form-panel"
            accordion
            bordered={false}
            className="new-checkup-collapse ant-card and-card-bordered"
        >
            <Panel
                key="analysis-form-panel"
                className="main-header"
                header={
                    <React.Fragment>
                        <div className="panel-header">Perform a new checkup</div>
                        <div className="panel-subheader">Get a full SEO Checkup for your website</div>
                    </React.Fragment>
                }
            >
                <Form
                    onSubmit={handleSubmitForm}
                    className="seo-checkup-form"
                >
                    <div className="half-grid">
                        <div className="grid-item">
                            <Form.Item
                                validateStatus={validationState.mainUrlValidation !== '' ? 'error' : 'validating'}
                                help={validationState.mainUrlValidation}
                            >
                                <label htmlFor="mainUrlInput">Your website</label>
                                <UrlInput
                                    id="mainUrlInput"
                                    placeholder="example.com"
                                    domain={urlInputState.domain}
                                    urlSchema={urlInputState.urlSchema}
                                    onUrlChange={handleUrlChange}
                                />
                            </Form.Item>
                        </div>

                        <div className="grid-item">
                            <SectionsSelector
                                selectedSectionTitles={selectedSectionTitles}
                                onSelectedSectionTitlesChange={setSelectedSectionTitles}
                            />
                        </div>
                    </div>

                    <div className="competitors-selection">
                        <CompetitorsInput
                            competitors={competitorsInputState}
                            competitorsValidation={validationState.competitorsValidation}
                            maxCompetitors={maxCompetitors ? maxCompetitors : 1}
                            onCompetitorsChange={handleCompetitorsChange}
                        />
                    </div>

                    <Button
                        type="primary"
                        htmlType="submit"
                        className="checkup-cta"
                    >
                        Checkup your SEO
                    </Button>
                </Form>
            </Panel>
        </Collapse>
    );
}
